// Pharmacy charge capture for the nurse's receipt confirmation.
//
// POST ipd_admission/{admission_id}/billing/pharmacy-charge-capture/ is meant
// to fire once the ward has ACCEPTED a delivery, so the patient is billed for
// what actually reached them. It was never wired to the nurse queue, so
// confirmed deliveries produced no charge at all.
//
// Billed quantity is the ACCEPTED quantity, not the delivered one. A nurse who
// accepts 8 of 10 has taken 8 onto the ward; billing the requested 10 would
// charge the patient for two units the pharmacy still holds. Fully rejected
// lines drop out entirely rather than posting a zero-quantity charge line.
//
// One item_id may arrive on several batches (different expiry/rate), and each
// batch bills as its own line — hence the nested `batches` array the endpoint
// expects.

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// The endpoint takes decimal strings, matching the shape the consumption flow
// already posts (inventory/utils/consumptionChargeCapture.js).
const toChargeDecimal = (value) => {
  const n = toNumber(value);
  return (n == null ? 0 : n).toFixed(4);
};

/**
 * @param lines the confirm dialog's per-line state, each carrying
 *              { item_id, item_name, batch_number, unit_rate, accepted,
 *                expiry_date? }
 * @returns { items: [{ item_id, item_name, batches: [...] }] }, or null when
 *          nothing was accepted — the caller should then skip the POST rather
 *          than send an empty charge.
 */
export function buildChargeCapturePayload(lines) {
  const byItem = new Map();

  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const itemId = line?.item_id;
    const accepted = toNumber(line?.accepted);
    // No item_id means nothing to bill against; 0 accepted means the ward took
    // nothing. Either way there is no charge to raise.
    if (!itemId || !accepted || accepted <= 0) return;

    if (!byItem.has(itemId)) {
      byItem.set(itemId, {
        item_id: itemId,
        item_name: line.item_name || "",
        batches: [],
      });
    }
    const batch = {
      quantity: toChargeDecimal(accepted),
      batch_number: line.batch_number || "",
      unit_rate: toChargeDecimal(line.unit_rate),
    };
    if (line.expiry_date) batch.expiry_date = line.expiry_date;
    byItem.get(itemId).batches.push(batch);
  });

  const items = Array.from(byItem.values());
  return items.length ? { items } : null;
}
